// Self-contained data for the Sidebar's bottom account block (identity chip +
// public/private mode toggle). Like usePrivateModeReason, this lets the chrome
// source its own state so AppShell doesn't have to thread account/mode props
// through every screen that mounts it (Dashboard, Swap, Send, Shield, …).
//
// The mode-switch logic mirrors useHomeDashboard.handleModeChange so toggling
// from the sidebar keeps the dashboard network filter consistent.
import { useCallback, useMemo } from 'react'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import {
  resolveDashboardNetworkFilterForModeChange
} from '@common/modules/dashboard/utils/dashboardModeNetworkFilter'
import { isExtension } from '@web/constants/browserapi'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getBalanceUsdByChain } from '@web/utils/railgunTokens'

const useSidebarAccount = () => {
  const { mode, setMode, ensureRailgunEngine } = useAppMode()
  const { dispatch } = useBackgroundService()
  const { account, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { networks } = useExtensionAwareNetworks()
  const { currentRailgunWallet } = useRailgunContext()

  const isPrivate = mode === ModeEnum.PRIVATE

  // ---- Identity ---------------------------------------------------------
  const accountName =
    account?.preferences?.label || (account?.addr ? shortenAddress(account.addr, 13) : '')
  // The public 0x address — ALWAYS used for the avatar (Jazzicon/blockie parse a
  // hex address and throw on a 0zk… one).
  const accountAddress = account?.addr || ''
  // Private mode shows the Railgun (0zk…) address as the chip label, public
  // shows the 0x address.
  const displayAddress =
    isPrivate && currentRailgunWallet?.railgunAddress
      ? currentRailgunWallet.railgunAddress
      : account?.addr || ''
  const accountPfp = account?.preferences?.pfp || account?.addr || ''
  const accountIsSmart = useMemo(() => (account ? isSmartAccount(account) : false), [account])

  // ---- Mode switch (mirrors useHomeDashboard.handleModeChange) -----------
  const firstPrivateSupportedChainId = useMemo(() => {
    const set = new Set(EXTENSION_PRIVATE_CHAIN_IDS.map((id) => id.toString()))
    const exists: Record<string, true> = {}
    networks.forEach((n) => {
      exists[n.chainId.toString()] = true
    })
    const balanceTokenMap = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    const usdByChain =
      balanceTokenMap && prices ? getBalanceUsdByChain(balanceTokenMap, prices) : undefined
    const sorted = networks
      .map((n) => n.chainId.toString())
      .filter((chainId) => set.has(chainId) && exists[chainId])
      .sort((a, b) => (usdByChain?.get(Number(b)) ?? 0) - (usdByChain?.get(Number(a)) ?? 0))
    return sorted.length ? sorted[0] : EXTENSION_PRIVATE_CHAIN_IDS[0]?.toString()
  }, [currentRailgunWallet?.balanceTokenMap, currentRailgunWallet?.balanceTokenPriceMap, networks])

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      const nextFilter = resolveDashboardNetworkFilterForModeChange({
        previousMode: mode,
        nextMode,
        currentFilter: dashboardNetworkFilter,
        isExtension,
        firstPrivateSupportedChainId
      })
      if (nextFilter !== undefined) {
        dispatch({
          type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
          params: { dashboardNetworkFilter: nextFilter }
        })
      }
      if (nextMode === ModeEnum.PRIVATE) {
        setTimeout(() => setMode(nextMode), 50)
      } else {
        setMode(nextMode)
      }
    },
    [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId, setMode]
  )

  return {
    accountName,
    accountAddress,
    displayAddress,
    accountPfp,
    accountIsSmart,
    handleModeChange,
    ensureRailgunEngine
  }
}

export default useSidebarAccount
